import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getCookieBanner, type CookieBannerConfig, type CookieTranslation } from "@/lib/cookieBanner.functions";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "pulso33_cookie_consent_v1";

type StoredConsent = {
  policy_version: string;
  timestamp: number;
  choices: Record<string, boolean>;
  dns?: boolean;
  limit_sensitive?: boolean;
};

type EnabledConfig = Extract<CookieBannerConfig, { enabled: true }>;

declare global {
  interface Window {
    pulso33Consent?: {
      get: () => StoredConsent | null;
      update: (choices: Record<string, boolean>, extras?: { dns?: boolean; limit_sensitive?: boolean }) => void;
      open: () => void;
      hasConsent: (category: string) => boolean;
    };
  }
}

function readStored(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
}

function writeStored(v: StoredConsent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {}
}

function needsPrompt(stored: StoredConsent | null, cfg: EnabledConfig): boolean {
  if (!stored) return true;
  if (stored.policy_version !== cfg.policy_version) return true;
  const ageDays = (Date.now() - stored.timestamp) / 86_400_000;
  if (ageDays > cfg.behavior.reshow_after_days) return true;
  return false;
}

function pickTranslation(cfg: EnabledConfig, lang: string): CookieTranslation {
  return (
    cfg.translations[lang] ??
    cfg.translations[cfg.default_locale] ??
    cfg.translations[Object.keys(cfg.translations)[0]!]
  );
}

function pickI18n(map: Record<string, string>, lang: string, defLang: string): string {
  return map[lang] ?? map[defLang] ?? Object.values(map)[0] ?? "";
}

function useResolvedTheme(theme: "light" | "dark" | "auto"): "light" | "dark" {
  const [resolved, setResolved] = useState<"light" | "dark">(theme === "auto" ? "dark" : theme);
  useEffect(() => {
    if (theme !== "auto") {
      setResolved(theme);
      return;
    }
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handle = () => setResolved(mq.matches ? "dark" : "light");
    handle();
    mq.addEventListener("change", handle);
    return () => mq.removeEventListener("change", handle);
  }, [theme]);
  return resolved;
}

export function CookieBanner() {
  const { lang } = useT();
  const fetcher = useServerFn(getCookieBanner);
  const { data } = useQuery({
    queryKey: ["cookie-banner", lang],
    queryFn: () => fetcher({ data: { locale: lang } }),
    staleTime: 60_000,
  });

  const [open, setOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [choices, setChoices] = useState<Record<string, boolean>>({});
  const [dns, setDns] = useState(false);
  const [limitSensitive, setLimitSensitive] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const cfg = data && data.enabled ? data : null;

  // Initialize state when config arrives
  useEffect(() => {
    if (!cfg) return;
    const stored = readStored();
    const initial: Record<string, boolean> = {};
    for (const c of cfg.categories) {
      // GDPR: non-required default OFF on first prompt
      initial[c.key] = c.required ? true : stored?.choices[c.key] ?? false;
    }
    setChoices(initial);
    setDns(stored?.dns ?? false);
    setLimitSensitive(stored?.limit_sensitive ?? false);
    if (needsPrompt(stored, cfg)) setOpen(true);
  }, [cfg]);

  const persist = useCallback(
    (
      nextChoices: Record<string, boolean>,
      extras?: { dns?: boolean; limit_sensitive?: boolean },
    ) => {
      if (!cfg) return;
      const payload: StoredConsent = {
        policy_version: cfg.policy_version,
        timestamp: Date.now(),
        choices: nextChoices,
        dns: extras?.dns,
        limit_sensitive: extras?.limit_sensitive,
      };
      writeStored(payload);
      window.dispatchEvent(new CustomEvent("pulso33:consent-change", { detail: payload }));
    },
    [cfg],
  );

  // Expose global API
  useEffect(() => {
    if (!cfg) return;
    window.pulso33Consent = {
      get: () => readStored(),
      update: (c, extras) => persist(c, extras),
      open: () => {
        const stored = readStored();
        if (stored) {
          setChoices({ ...stored.choices });
          setDns(stored.dns ?? false);
          setLimitSensitive(stored.limit_sensitive ?? false);
        }
        setCustomizing(true);
        setOpen(true);
      },
      hasConsent: (category) => {
        const s = readStored();
        return !!s?.choices[category];
      },
    };
  }, [cfg, persist]);

  const acceptAll = () => {
    if (!cfg) return;
    const next: Record<string, boolean> = {};
    for (const c of cfg.categories) next[c.key] = true;
    setChoices(next);
    persist(next, { dns, limit_sensitive: limitSensitive });
    setOpen(false);
    setCustomizing(false);
  };

  const rejectAll = () => {
    if (!cfg) return;
    const next: Record<string, boolean> = {};
    for (const c of cfg.categories) next[c.key] = c.required;
    setChoices(next);
    persist(next, { dns: true, limit_sensitive: true });
    setOpen(false);
    setCustomizing(false);
  };

  const savePrefs = () => {
    if (!cfg) return;
    persist(choices, { dns, limit_sensitive: limitSensitive });
    setOpen(false);
    setCustomizing(false);
  };

  // Focus trap + ESC handling
  useEffect(() => {
    if (!open || !cfg) return;
    const node = dialogRef.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (cfg.behavior.block_until_consent && !readStored()) {
          e.preventDefault();
          return;
        }
        setOpen(false);
        return;
      }
      if (e.key !== "Tab" || !node) return;
      const focusables = node.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    setTimeout(() => node?.querySelector<HTMLElement>("button, a, input")?.focus(), 0);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, cfg]);

  const theme = useResolvedTheme(cfg?.appearance.theme ?? "auto");

  const tr = useMemo(() => (cfg ? pickTranslation(cfg, lang) : null), [cfg, lang]);

  if (!cfg || !tr || !open) return null;

  const { appearance, behavior, regions, controller, categories } = cfg;
  const showCcpa = regions.includes("us_ca");
  const usesModal = appearance.position === "modal" || behavior.block_until_consent;
  const themeClasses =
    theme === "dark" ? "bg-neutral-900 text-neutral-50 border-neutral-700" : "bg-white text-neutral-900 border-neutral-200";
  const subTextClass = theme === "dark" ? "text-neutral-300" : "text-neutral-600";

  const positionClasses = usesModal
    ? "fixed inset-0 z-[100] flex items-center justify-center p-4"
    : appearance.position === "top"
      ? "fixed inset-x-0 top-0 z-[100] p-4"
      : "fixed inset-x-0 bottom-0 z-[100] p-4";

  const accent = appearance.accent_color || "#0ea5e9";

  const Btn = ({
    onClick,
    variant,
    children,
  }: {
    onClick: () => void;
    variant: "primary" | "outline";
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex-1 min-w-[140px] rounded-md px-4 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2",
      )}
      style={
        variant === "primary"
          ? { backgroundColor: accent, color: "#fff" }
          : {
              backgroundColor: "transparent",
              color: theme === "dark" ? "#fff" : "#111",
              border: `1px solid ${accent}`,
            }
      }
    >
      {children}
    </button>
  );

  return (
    <>
      {usesModal && (
        <div
          className="fixed inset-0 z-[99] bg-black/60"
          aria-hidden="true"
          onClick={() => {
            if (!behavior.block_until_consent) setOpen(false);
          }}
        />
      )}
      <div className={positionClasses}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal={usesModal}
          aria-labelledby="pulso33-cookie-title"
          aria-describedby="pulso33-cookie-desc"
          className={cn(
            "mx-auto w-full max-w-2xl rounded-lg border shadow-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto",
            themeClasses,
          )}
        >
          <h2 id="pulso33-cookie-title" className="text-lg font-bold mb-2 text-accent-foreground">
            {tr.title}
          </h2>
          <p id="pulso33-cookie-desc" className={cn("text-sm leading-relaxed mb-4", subTextClass)}>
            {tr.description}{" "}
            <a
              href={tr.policy_link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: accent }}
            >
              {tr.policy_link_label}
            </a>
          </p>

          {!customizing ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Btn onClick={acceptAll} variant="primary">
                {tr.accept_all}
              </Btn>
              {behavior.show_reject_all && (
                <Btn onClick={rejectAll} variant="primary">
                  {tr.reject_all}
                </Btn>
              )}
              {behavior.show_customize && (
                <Btn onClick={() => setCustomizing(true)} variant="primary">
                  {tr.customize}
                </Btn>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                {categories.map((c) => {
                  const checked = !!choices[c.key];
                  return (
                    <label
                      key={c.key}
                      className={cn(
                        "flex items-start gap-3 rounded border p-3",
                        theme === "dark" ? "border-neutral-700" : "border-neutral-200",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={c.required ? true : checked}
                        disabled={c.required}
                        onChange={(e) =>
                          setChoices((prev) => ({ ...prev, [c.key]: e.target.checked }))
                        }
                        className="mt-1 h-4 w-4"
                        style={{ accentColor: accent }}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-semibold">
                          {pickI18n(c.label_i18n, lang, cfg.default_locale)}
                          {c.required && (
                            <span className={cn("ml-2 text-xs", subTextClass)}>
                              (always on)
                            </span>
                          )}
                        </div>
                        <div className={cn("text-xs mt-1", subTextClass)}>
                          {pickI18n(c.description_i18n, lang, cfg.default_locale)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {showCcpa && (
                <div
                  className={cn(
                    "space-y-2 rounded border p-3",
                    theme === "dark" ? "border-neutral-700" : "border-neutral-200",
                  )}
                >
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={dns}
                      onChange={(e) => setDns(e.target.checked)}
                      className="mt-1 h-4 w-4"
                      style={{ accentColor: accent }}
                    />
                    <span className="text-sm">{tr.dns_label}</span>
                  </label>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={limitSensitive}
                      onChange={(e) => setLimitSensitive(e.target.checked)}
                      className="mt-1 h-4 w-4"
                      style={{ accentColor: accent }}
                    />
                    <span className="text-sm">{tr.limit_sensitive_label}</span>
                  </label>
                </div>
              )}

              <div className={cn("text-xs space-y-0.5 pt-2 border-t", theme === "dark" ? "border-neutral-700" : "border-neutral-200", subTextClass)}>
                <div className="font-semibold">{controller.company_name}</div>
                <div>{controller.legal_address}</div>
                <div>
                  <a href={`mailto:${controller.contact_email}`} className="underline">
                    {controller.contact_email}
                  </a>
                </div>
                {controller.dpo_email && (
                  <div>
                    DPO:{" "}
                    <a href={`mailto:${controller.dpo_email}`} className="underline font-semibold">
                      {controller.dpo_email}
                    </a>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Btn onClick={savePrefs} variant="primary">
                  {tr.save_preferences}
                </Btn>
                <Btn onClick={acceptAll} variant="outline">
                  {tr.accept_all}
                </Btn>
                {behavior.show_reject_all && (
                  <Btn onClick={rejectAll} variant="outline">
                    {tr.reject_all}
                  </Btn>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** Helper for non-React code paths. */
export function hasConsent(category: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const s = JSON.parse(raw) as StoredConsent;
    return !!s.choices[category];
  } catch {
    return false;
  }
}
